import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon.tsx";
import Card from "components/Card/Card";
import Drawer from "components/Drawer/Drawer.tsx";
import BulkImportRREModal from "components/Modals/BulkImportRREModal";
import KaboodleModal from "components/Modals/KaboodleModal";
import Sidebar from "components/Sidebar/Sidebar.tsx";
import { Tooltip } from "components/Tooltip/Tooltip";
import { FEATURE_FLAGS } from "constants/FeatureFlags";
import AckEmailModal from "pages/files/sections/AckEmailModal";
import EmailTypeSelectionModal from "pages/files/sections/EmailTypeSelectionModal";
import RatedAgeRequestModal from "pages/files/sections/RatedAgeRequestModal";
import RequestMaterialsModal, {
  type RequestMaterialsServiceOption,
} from "pages/files/sections/RequestMaterialsModal";
import GenerateAllocatorNotesButton from "pages/future-medical/shared/right-sidebar/allocator-notes/GenerateAllocatorNotesButton";
import GenerateDocumentButton from "pages/future-medical/shared/right-sidebar/document-generation/GenerateDocumentButton";
import GenerateLienDocumentButton from "pages/liens/document-generation/GenerateLienDocumentButton";
import { useLienFileData } from "pages/liens/hooks/useLienFileData";
import TriggerOCRModal from "pages/liens/trigger-ocr/TriggerOCRModal";
import GenerateCombinedPdfButton from "pages/metadoc/document-generation/GenerateCombinedPdfButton";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  setShowArchiveModal,
  setShowResolveCMSErrorModal,
} from "reducers/claims";
import {
  closeAllDrawers,
  setErrorDrawerVisible,
  setShowBackgroundJobs,
  setShowClaimLinkingDrawer,
  setShowClientDocumentsDrawer,
  setShowClientInfoDrawer,
  setShowClientExportFeedsDrawer,
  setShowClientFeedsDrawer,
  setShowClientFilesDrawer,
  setShowClinicianNotes,
  setShowCPTCodesDrawer,
  setShowDocumentsDrawer,
  setShowEmailDrawer,
  setShowLegalNotes,
  setShowLienICDCodingDrawer,
  setShowLienPaymentHistoryDrawer,
  setShowMedicalHistory,
  setShowRelatedFilesDrawer,
  setShowReportsTableDrawer,
  setShowRxHistory,
  setShowTasksDrawer,
  setWarningDrawerVisible,
} from "reducers/drawer";
import { setShowArchiveModal as setShowFileArchiveModal } from "reducers/files";
import { setShowAddCoverageModal } from "reducers/future-medical";
import {
  setCurrentLienStatus,
  setShowCreateLienModal,
  updateLienStatus,
} from "reducers/liens";
import {
  setDownloadTemplate,
  setShowAddModal,
  setShowCreateReReviewModal,
  setShowSendToOpsModal,
  setShowTaskModal,
  setShowUploadClaimsOnFileModal,
  setShowUploadTemplateModal,
} from "reducers/main";
import {
  setShowAddPolicyModal,
  setShowAddRRETagModal,
  setShowDownloadModal,
  setShowSendCMSFileModal,
  setShowUploadModal,
} from "reducers/rre";
import { useGetClaimByIdQuery } from "services/claims-api";
import { useDeleteClientMutation } from "services/client-api";
import { useGetDocumentManagementFileByIdQuery } from "services/document-management-api";
import { useGetFileByIdQuery } from "services/file-api";
import {
  useDeleteLienByIdMutation,
  useGetLienByIdQuery,
  usePatchLienByIdMutation,
} from "services/lien-api";
import {
  useCreateCMSDevelopmentMutation,
  useCreateMSADecisionMutation,
  useGetMSAByIdQuery,
  useGetMSADecisionsQuery,
} from "services/msa-api";
import type {
  AmendedReviewScreeningType,
  CostProjectionDataType,
  FileType,
  MSADataType,
  MSADecisionType,
  PaginatedResponse,
} from "types/DataTypes";
import { useConstants } from "utils/hooks";
import { useSelector } from "utils/redux";

import { KaboodleToaster } from "../common";
import OCRLetterModal from "../Modals/OCRLetterModal";
import OCRQueryResultModal from "../Modals/OCRQueryResultModal";
import OCRRatedAgeModal from "../Modals/OCRRatedAgeModal";
import FutureMedicalReviewButtons from "./components/FutureMedicalReviewButtons";
import LienStatusTrafficLight from "./components/LienStatusTrafficLight";
import ServiceStatusToggle from "./components/ServiceStatusToggle";
import { getEmailFlowToOpen } from "./emailFlowSelection";

type KaboodleRightSideBarPropTypes = {
  isVisible?: boolean;
};

type ButtonRenderSettingsType = {
  shouldRender: boolean;
  content: JSX.Element;
};

const KaboodleRightSideBar = ({ isVisible }: KaboodleRightSideBarPropTypes) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const url = location.pathname;
  const prevPathRef = useRef(location.pathname);
  const { constants } = useConstants();
  const navigate = useNavigate();

  // helper function to handle right-click on drawer buttons
  const handleDrawerContextMenu = (
    e: React.MouseEvent<HTMLButtonElement>,
    drawerParam: string,
  ) => {
    e.preventDefault();
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    const separator = currentUrl.includes("?") ? "&" : "?";
    const url = `${currentUrl}${separator}drawer=${drawerParam}`;
    window.open(url, "_blank");
  };

  const { validationWarnings, validationErrors } = useSelector(
    (state) => state.main,
  );

  const {
    errorDrawerVisible,
    warningDrawerVisible,
    showDocumentsDrawer,
    showLegalNotes,
    showMedicalHistory,
    showRxHistory,
    showClinicianNotes,
    showTasksDrawer,
    showClaimLinkingDrawer,
    showRelatedFilesDrawer,
    showEmailDrawer,
    showLienPaymentHistoryDrawer,
    showReportsTableDrawer,
    showLienICDCodingDrawer,
    showBackgroundJobs,
    showClientInfoDrawer,
    showClientFilesDrawer,
    showClientDocumentsDrawer,
    showClientFeedsDrawer,
    showClientExportFeedsDrawer,
    showCPTCodesDrawer,
  } = useSelector((state) => state.drawer);

  const { fileId } = useParams<{ fileId: string }>();
  const { claimId } = useParams<{ claimId: string }>();
  const { documentManagementFileId } = useParams<{
    documentManagementFileId: string;
  }>();
  const { metadocId } = useParams<{ metadocId: string }>();

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );
  const fileStatus = fileData?.status;

  const { data: claimData } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: !claimId },
  );
  const claimHasCmsErrors = claimData?.has_cms_errors_to_resolve;

  const { data: documentManagementFileData } =
    useGetDocumentManagementFileByIdQuery(
      { documentManagementFileId: documentManagementFileId },
      { skip: !documentManagementFileId },
    );

  // Liens state
  const { currentLienStatus } = useSelector((state) => state.liens);

  // Files state
  const { selectedFileIds } = useSelector((state) => state.files);

  // RRE state
  const currentFile = useSelector((state) => state.files.currentFile);
  const fileHasSection111Service =
    currentFile?.services?.filter((service) => service?.type === "111").length >
    0;

  // Claims state
  const { showArchiveModal: showClaimArchiveModal } = useSelector(
    (state) => state.claim,
  );

  // MSA document generation
  const msaIdMatch = url.match(/\/msa\/(\d+)/);
  const msaId = msaIdMatch ? msaIdMatch[1] : undefined;
  const showFilesArchiveModal = useSelector(
    (state) => state.files.showArchiveModal,
  );
  const [createCMSDevelopment] = useCreateCMSDevelopmentMutation();
  const [createMSADecision] = useCreateMSADecisionMutation();

  // Lien document generation - removed unused generateLienDocument mutation

  // Reporting
  const isSection111Reporting = url.includes("/section-111/reporting");

  // Finance
  const isFinancePage =
    url.includes("/reporting") && !url.includes("/section-111/reporting");

  // Claims
  const isClaimsPage = url.includes("/claims");
  const isClaimsOverviewPage = !!url.match(/\/claims\/\d+\/overview$/);
  const isClaimDetailPage = !!url.match(/\/claims\/\d+/);
  const isClaimICDCodingPage = !!url.match(/\/claims\/\d+\/icd-coding$/);
  const isClaimAuditHistoryPage = !!url.match(/\/claims\/\d+\/audit-history$/);
  const isClaimTranmissionHistoryPage = !!url.match(
    /\/claims\/\d+\/transmission-history$/,
  );
  const isClaimQueryResponsePage = !!url.match(
    /\/claims\/\d+\/query-response$/,
  );
  const isClaimCMPPage = !!url.match(/\/claims\/\d+\/cmp$/);

  // Future Medical
  const isMSAPage = url.includes("/future-medical/msa/");
  const isMSAOverviewPage = url.includes("/future-medical/overview");
  const isMSARatedAgePage = url.includes("/rated-age");
  const isMSALegalReviewPage =
    url.includes("/future-medical/msa") && url.includes("/legal-review");
  const isMSAMedicalReviewPage =
    url.includes("/future-medical/msa") && url.includes("/medical-review");
  const isMSAQueryPage =
    url.includes("/future-medical/msa") && url.includes("/query");

  // Amended Review
  const isAmendedReviewPage = url.includes("/future-medical/amended-review");
  const isAmendedReviewQueryPage =
    url.includes("/future-medical/amended-review") && url.includes("/query");
  const isAmendedReviewLegalReviewPage =
    url.includes("/future-medical/amended-review") &&
    url.includes("/legal-review");
  const isAmendedReviewMedicalReviewPage =
    url.includes("/future-medical/amended-review") &&
    url.includes("/medical-review");
  const amendedReviewMatch = url.match(/\/amended-review\/(\d+)/);
  const amendedReviewId = amendedReviewMatch
    ? amendedReviewMatch[1]
    : undefined;

  const isAmendedReviewScreenPage = url.includes(
    "/future-medical/ar-screening",
  );

  // Cost Projection
  const isMCPPage = url.includes("/future-medical/mcp/");
  const isLCPPage = url.includes("/future-medical/lcp/");
  const lcpIdMatch = url.match(/\/lcp\/(\d+)/);
  const lcpId = lcpIdMatch ? lcpIdMatch[1] : undefined;
  const mcpIdMatch = url.match(/\/mcp\/(\d+)/);
  const mcpId = mcpIdMatch ? mcpIdMatch[1] : undefined;
  const isCostProjectionPage = isMCPPage || isLCPPage;
  const isCostProjectionQueryPage =
    (isMCPPage || isLCPPage) && url.includes("/query");
  const isCostProjectionMedicalReviewPage =
    (isMCPPage || isLCPPage) && url.includes("/medical-review");
  const isCostProjectionLegalReviewPage =
    (isMCPPage || isLCPPage) && url.includes("/legal-review");

  const isMsaSubPagePattern = /\/msa\/[^/]+\/[^/]+$/;
  const isMsaSubPage = isMsaSubPagePattern.test(url);

  const isCMSStatusPage = url.includes("/cms-status");

  // RRE
  const isRrePage = url.includes("/rres");
  const isRreOverviewPage = !!url.match(/\/rres\/\d+\/overview$/);
  const isRreDetailPage = !!url.match(/\/rres\/\d+/);
  const isRreTagsPage = url.endsWith("/tags") && url.includes("rres");
  const isRreCMSSubmissionsPage =
    url.includes("/cms-submissions") && url.includes("rres");
  const isRreCMSCommunicationPage =
    url.includes("/cms-communication") && url.includes("rres");
  const isRrePolicyPage = url.includes("/policies") && url.includes("rres");
  const isRreImportPage = url.includes("/imports") && url.includes("rres");
  const isRreTinsPage = url.endsWith("/tins") && url.includes("rres");

  // File
  const isFilesListingPage = url === "/operations/files";
  const isIntakeListingPage = url === "/operations/intake";
  const isFileEditPage = /^\/operations\/files\/\d+\/edit(\/[^/]+)?$/.test(url);
  const isFileQueryPage =
    url.includes("/operations/files/") && url.includes("/edit/query");

  // Meta Documents - only show on metadoc page
  const isMetaDocPage = url.includes("/metadoc");
  const isMSAMetadocDetailPage =
    isMSAPage && Boolean(msaId) && isMetaDocPage && Boolean(metadocId);
  const isOperationsMetadocDetailPage =
    url.includes("/operations/files/") && isMetaDocPage && Boolean(metadocId);
  const isFutureMedicalServiceDetailPage =
    /\/future-medical\/(msa|amended-review|ar-screening|mcp|lcp)\/\d+/.test(
      url,
    ) && !isMetaDocPage;

  // Client
  const isClientPage = url.endsWith("/clients");
  const isClientDetailPage = !!url.match(/\/clients\/\d+\/(overview)$/);
  const isClientInvoicingPage = !!url.match(
    /\/clients\/\d+\/invoicing|client-invoicing$/,
  );

  // Lien
  const isLienDetailPage = url.includes("/liens/") && url.includes("/lien/");
  const isLienOverviewPage = !!url.match(/\/liens\/\d+\/overview$/);
  const isLienQueryPage = url.includes("/liens/") && url.includes("/query");
  const lienIdMatch = url.match(/\/lien\/(\d+)/);
  const lienId = lienIdMatch ? lienIdMatch[1] : undefined;
  // Removed unused lienfileId variable

  // Document Management
  const isDocumentManagementPage = url.includes("/document-management");
  const documentManagementIdMatch = url.match(/\/document-management\/(\d+)/);
  const documentManagementId = documentManagementIdMatch
    ? documentManagementIdMatch[1]
    : undefined;

  const { lienFileData, workspaceUrl } = useLienFileData();

  // Audit or Consulting
  const isAuditOrConsultingPage =
    (url.includes("/audit") || url.includes("/consulting")) &&
    !url.includes("/audit-history");

  const isEmployersPage = url.endsWith("/employers");
  const isClaimantsPage = url.endsWith("/claimants");
  const isPoliciesPage = url.endsWith("/policies");
  const isImportsPage = url.endsWith("/imports");
  const isYourTasksPage = url.endsWith("/your-tasks");
  // const isICDCodingPage = url.endsWith("/icd-coding");
  const showMSAButtons = isMSAPage || isMSAOverviewPage;

  // get MSAData
  const shouldFetchMSA = Boolean(isCMSStatusPage && (msaId || amendedReviewId));
  const { data: msaData } = useGetMSAByIdQuery(
    {
      msaId: msaId || amendedReviewId,
    },
    {
      skip: !shouldFetchMSA,
    },
  );

  // Gates the OCR MSA decision letter button. Also queried by CMSStatus.tsx for the same msaId; RTK Query
  // dedupes identical concurrent queries, so this isn't an extra request.
  const { data: msaDecisionsResponse } = useGetMSADecisionsQuery(
    { msaId: (msaId || amendedReviewId) as string },
    { skip: !shouldFetchMSA },
  );
  const msaDecisions = (
    msaDecisionsResponse as unknown as PaginatedResponse<MSADecisionType>
  )?.results;
  // More than one undecided decision can exist at once, so this takes the latest one, relying on the backend
  // ordering decisions by created ascending.
  const undecidedMSADecisions = msaDecisions?.filter(
    (d) => d.decision_type === null,
  );
  const undecidedMSADecision =
    undecidedMSADecisions?.[undecidedMSADecisions.length - 1];

  // get the coverage state from redux
  const { noCoverage, coverageComplete, showAddCoverageModal } = useSelector(
    (state) => state.futureMedical,
  );

  // get current service from future-medical reducer
  const { currentService } = useSelector((state) => {
    return state.futureMedical;
  });

  const requestMaterialsCurrentService = currentService as
    | MSADataType
    | CostProjectionDataType
    | AmendedReviewScreeningType
    | null;
  const requestMaterialsServiceId =
    typeof requestMaterialsCurrentService?.service === "number"
      ? String(requestMaterialsCurrentService.service)
      : undefined;
  // The number branch below is a defensive fallback, not a known live path.
  const requestMaterialsServiceFile = currentService?.file as
    | number
    | FileType
    | undefined;
  const requestMaterialsFile =
    typeof requestMaterialsServiceFile === "object"
      ? requestMaterialsServiceFile
      : undefined;
  const requestMaterialsFallbackEmail =
    requestMaterialsFile?.source === "INTAKE" &&
    typeof requestMaterialsFile?.creator === "object"
      ? requestMaterialsFile.creator?.email
      : undefined;
  const requestMaterialsDefaultToEmail =
    requestMaterialsFile?.referring_party_email ??
    requestMaterialsFallbackEmail;
  const requestMaterialsFileId = requestMaterialsFile?.id
    ? String(requestMaterialsFile.id)
    : typeof requestMaterialsServiceFile === "number"
      ? String(requestMaterialsServiceFile)
      : undefined;
  const requestMaterialsServiceType = isAmendedReviewScreenPage
    ? "ARS"
    : isMSAPage || isAmendedReviewPage
      ? (currentService as MSADataType | null)?.type_code
      : currentService?.type;

  const [showOCRModal, setShowOCRModal] = useState(false);
  const [showOCRProgressModal, setShowOCRProgressModal] = useState(false);
  const [showDeleteLienModal, setShowDeleteLienModal] = useState(false);
  const [showDeleteClientModal, setShowDeleteClientModal] = useState(false);
  const [showBulkImportRREModal, setShowBulkImportRREModal] = useState(false);
  const [showOCRQueryResultModal, setShowOCRQueryResultModal] = useState(false);
  const [showOCRRatedAgeModal, setShowOCRRatedAgeModal] = useState(false);
  const [showOCRMSADecisionModal, setShowOCRMSADecisionModal] = useState(false);
  const [showAckEmailModal, setShowAckEmailModal] = useState(false);
  const [showRequestMaterialsModal, setShowRequestMaterialsModal] =
    useState(false);
  const [
    showRequestMaterialsFromFileViewModal,
    setShowRequestMaterialsFromFileViewModal,
  ] = useState(false);
  const [showRatedAgeRequestModal, setShowRatedAgeRequestModal] =
    useState(false);
  const [showEmailTypeSelectionModal, setShowEmailTypeSelectionModal] =
    useState(false);

  const loggedInUser = useSelector((state) => state.main.loggedInUser);
  const canSendAckEmail =
    isFileEditPage &&
    Boolean(loggedInUser?.has_operations_authority) &&
    Boolean(
      constants?.FEATURE_FLAGS?.[
        FEATURE_FLAGS.REFERRAL_ACKNOWLEDGEMENT_EMAIL_ENABLED
      ],
    );
  const canSendRatedAgeRequest =
    isFileEditPage &&
    Boolean(loggedInUser?.has_operations_authority) &&
    Boolean(
      constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.RATED_AGE_REQUEST_ENABLED],
    );
  // Keep in sync with VALID_REQUEST_FOR_MATERIALS_SERVICE_TYPES in emails.
  const REQUEST_FOR_MATERIALS_SERVICE_TYPES = [
    "MSA",
    "MSA-111",
    "NTMSA",
    "PRE-MSA",
    "ARV",
    "MCP",
    "LCP",
    "ARS",
  ];
  const requestForMaterialsFileServiceOptions: RequestMaterialsServiceOption[] =
    (fileData?.services ?? [])
      .filter((service) =>
        REQUEST_FOR_MATERIALS_SERVICE_TYPES.includes(service.type),
      )
      .map((service) => ({
        id: String(service.id),
        type: service.type,
        label: `${service.type_verbose} (#${service.id})`,
      }));
  const canSendRequestForMaterials =
    isFileEditPage &&
    Boolean(loggedInUser?.has_operations_authority) &&
    Boolean(
      constants?.FEATURE_FLAGS?.[
        FEATURE_FLAGS.REQUEST_FOR_MATERIALS_EMAIL_ENABLED
      ],
    ) &&
    requestForMaterialsFileServiceOptions.length > 0;
  const { clientId } = useParams<{ clientId: string }>();
  const [deleteClient] = useDeleteClientMutation();

  const handleAddDevelopment = async () => {
    try {
      await createCMSDevelopment({
        msaId: msaId == null ? amendedReviewId : msaId,
      }).unwrap();
      KaboodleToaster.success("Development created successfully");
    } catch (error) {
      const errMsg = `Failed to create development: ${error.data.detail}`;
      KaboodleToaster.error(errMsg);
    }
  };

  const handleAddDecision = async () => {
    try {
      await createMSADecision({
        msaId: msaId == null ? amendedReviewId : msaId,
      }).unwrap();
      KaboodleToaster.success("Decision created successfully");
    } catch (error) {
      const errMsg = `Failed to create development: ${error.data.detail}`;
      KaboodleToaster.error(errMsg);
    }
  };

  // get current lien data for the lien status buttons
  const { data: lienData } = useGetLienByIdQuery(
    { lienId },
    { skip: !isLienDetailPage || !lienId },
  );

  const [patchLien] = usePatchLienByIdMutation();
  const [deleteLien] = useDeleteLienByIdMutation();

  useEffect(() => {
    if (lienData) {
      dispatch(
        setCurrentLienStatus({
          analysis_complete: lienData.analysis_complete,
          client_submission: lienData.client_submission,
          case_closed: lienData.case_closed,
        }),
      );
    }
  }, [lienData, dispatch]);

  // close all drawers (main and validation) when URL changes
  useEffect(() => {
    // only close if the pathname has changed, not on initial render
    if (prevPathRef.current !== location.pathname) {
      dispatch(closeAllDrawers());
    }

    // update the path reference
    prevPathRef.current = location.pathname;
  }, [location.pathname, dispatch]);

  const handleLienStatusChange = async (
    field: "analysis_complete" | "client_submission" | "case_closed",
    value: boolean,
  ) => {
    if (!lienId) return;

    try {
      // opto update redux
      dispatch(updateLienStatus({ field, value }));

      await patchLien({
        lienId,
        data: { [field]: value },
      }).unwrap();

      // construct message based on field and value
      let message = "";

      if (field === "analysis_complete") {
        message = value
          ? "Analysis marked as complete"
          : "Analysis marked as incomplete";
      } else if (field === "client_submission") {
        message = value
          ? "Client submission marked as complete"
          : "Client submission marked as incomplete";
      } else {
        message = value ? "Case marked as closed" : "Case marked as open";
      }

      KaboodleToaster.success(message);
    } catch (error) {
      // revert the redux store if the API call fails
      dispatch(updateLienStatus({ field, value: !value }));
      console.error(`Failed to update ${field}:`, error);
      KaboodleToaster.error(`Failed to update ${field}`);
    }
  };

  const handleDeleteLien = async () => {
    if (!lienId) return;
    try {
      await deleteLien({
        lienId,
      }).unwrap();
      setShowDeleteLienModal(false);
      KaboodleToaster.success("Lien deleted successfully");
      // redirect to the liens overview page using the workspace URL from the hook
      if (workspaceUrl) {
        const liensOverviewUrl = workspaceUrl.replace(
          /\/lien\/\d+.*/,
          "/overview",
        );
        navigate(liensOverviewUrl);
      } else {
        navigate("/your-tasks");
      }
    } catch (error) {
      KaboodleToaster.error("Failed to delete lien");
    }
  };

  const handleDeleteClient = async () => {
    if (!clientId) return;

    try {
      await deleteClient({ id: clientId }).unwrap();
      setShowDeleteClientModal(false);
      navigate("/operations/clients");
      KaboodleToaster.success("Client deleted successfully");
    } catch (error) {
      console.error("Failed to delete client:", error);
      KaboodleToaster.error(error);
    }
  };

  // show validation drawer if on claim detail page or msa page or rre detail page or file edit page
  const shouldShowValidation = useMemo(() => {
    return (
      isClaimDetailPage ||
      isMSALegalReviewPage ||
      isAmendedReviewLegalReviewPage ||
      isRreDetailPage ||
      isFileEditPage ||
      isMsaSubPage ||
      isLienDetailPage ||
      isLienOverviewPage ||
      isMSAOverviewPage ||
      isCostProjectionPage ||
      isAmendedReviewPage ||
      isAmendedReviewScreenPage
    );
  }, [
    isClaimDetailPage,
    isMSALegalReviewPage,
    isAmendedReviewLegalReviewPage,
    isRreDetailPage,
    isFileEditPage,
    isMsaSubPage,
    isLienDetailPage,
    isLienOverviewPage,
    isMSAOverviewPage,
    isCostProjectionPage,
    isAmendedReviewPage,
    isAmendedReviewScreenPage,
  ]);

  const renderArchiveClaimButton = useMemo(() => {
    return (
      <>
        <Tooltip
          content={showClaimArchiveModal ? "Close Archive" : "Archive File"}
        >
          <ButtonIcon
            kind="neutral"
            onClick={() => {
              if (isClaimsOverviewPage) {
                dispatch(setShowArchiveModal(!showClaimArchiveModal));
              } else {
                dispatch(setShowFileArchiveModal(!showFilesArchiveModal));
              }
            }}
          >
            archive
          </ButtonIcon>
        </Tooltip>
      </>
    );
  }, [
    isFileEditPage,
    isClaimsOverviewPage,
    showClaimArchiveModal,
    showFilesArchiveModal,
  ]);

  const renderPaymentHistoryButton = (
    <Tooltip content="Lien Itemization">
      <ButtonIcon
        kind={showLienPaymentHistoryDrawer ? "active" : "neutral"}
        onClick={() => {
          dispatch(
            setShowLienPaymentHistoryDrawer(!showLienPaymentHistoryDrawer),
          );
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          const currentUrl = `${window.location.pathname}${window.location.search}`;
          const separator = currentUrl.includes("?") ? "&" : "?";
          const url = `${currentUrl}${separator}drawer=lienPaymentHistory`;
          window.open(url, "_blank");
        }}
      >
        attach_money
      </ButtonIcon>
    </Tooltip>
  );

  const renderBackgroundJobsButton = (
    <Tooltip content="Background Jobs">
      <ButtonIcon
        kind={showBackgroundJobs ? "active" : "neutral"}
        onClick={() => {
          dispatch(setShowBackgroundJobs(!showBackgroundJobs));
        }}
        onContextMenu={(e) => handleDrawerContextMenu(e, "backgroundJobs")}
      >
        work_history
      </ButtonIcon>
    </Tooltip>
  );

  /*
  Settings for button renders should be added here by specifying a new entry to the object mapping
  The entry will map the name of the action to the ButtonRenderSettingsType for that button
  The settings determine the condition for which the button should be rendered and the content for the button that will be rendered if the condition is met.
  */
  const buttonRenderSettings = useMemo((): Record<
    string,
    ButtonRenderSettingsType
  > => {
    // The jsx for the different are defined here to be used in the map below
    const renderAddButton = (
      <Tooltip
        content={
          !showAddCoverageModal
            ? coverageComplete
              ? "Coverage marked complete"
              : noCoverage &&
                  (isMSAQueryPage ||
                    isCostProjectionQueryPage ||
                    isAmendedReviewQueryPage ||
                    isLienQueryPage ||
                    isFileQueryPage)
                ? "No coverage selected"
                : "Add"
            : "Add"
        }
        place="left"
        positionStrategy="fixed"
      >
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            if (isRreTagsPage) {
              dispatch(setShowAddRRETagModal(true));
            } else if (
              isMSAQueryPage ||
              isCostProjectionQueryPage ||
              isAmendedReviewQueryPage ||
              isLienQueryPage ||
              isFileQueryPage
            ) {
              dispatch(setShowAddCoverageModal(true));
            } else if (isLienOverviewPage) {
              dispatch(setShowCreateLienModal(true));
            } else {
              dispatch(setShowAddModal(true));
            }
          }}
          disabled={
            (isMSAQueryPage ||
              isCostProjectionQueryPage ||
              isAmendedReviewQueryPage ||
              isLienQueryPage ||
              isFileQueryPage) &&
            (coverageComplete || noCoverage)
          }
        >
          add
        </ButtonIcon>
      </Tooltip>
    );

    const renderAddTaskButton = (
      <Tooltip content="Add Task">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowTaskModal(true));
          }}
        >
          add_task
        </ButtonIcon>
      </Tooltip>
    );

    const renderSendToOpsButton = (
      <Tooltip content="Send To Ops">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowSendToOpsModal(true));
          }}
        >
          send
        </ButtonIcon>
      </Tooltip>
    );

    const renderUploadClaimsOnFileButton = (
      <Tooltip content="Upload Claims">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowUploadClaimsOnFileModal(true));
          }}
        >
          upload
        </ButtonIcon>
      </Tooltip>
    );

    const renderAddMSAReReviewButton = (
      <Tooltip content="Add ReReview">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowCreateReReviewModal(true));
          }}
        >
          add_task
        </ButtonIcon>
      </Tooltip>
    );

    const renderErrorButton = (
      <>
        <Tooltip content="Validation Errors">
          <ButtonIcon
            className="error-icon-button"
            iconType="text"
            kind={validationErrors?.length === 0 ? "neutral" : "error"}
            onClick={() => {
              dispatch(setErrorDrawerVisible(!errorDrawerVisible));
            }}
            disabled={validationErrors?.length === 0}
          >
            <span className="error-count">{validationErrors?.length || 0}</span>
            <span className="error-label">actions</span>
          </ButtonIcon>
        </Tooltip>
      </>
    );

    const renderWarningButton = (
      <>
        <Tooltip content="Warnings">
          <ButtonIcon
            className="warning-icon-button"
            iconType="text"
            kind={validationWarnings?.length === 0 ? "neutral" : "warning"}
            onClick={() => {
              dispatch(setWarningDrawerVisible(!warningDrawerVisible));
            }}
            disabled={validationWarnings?.length === 0}
          >
            <span className="warning-count">
              {validationWarnings?.length || 0}
            </span>
            <span className="warning-label">warnings</span>
          </ButtonIcon>
        </Tooltip>
      </>
    );

    const renderUploadButton = (
      <Tooltip content="Upload">
        <ButtonIcon
          kind="neutral"
          onClick={() => dispatch(setShowUploadModal(true))}
        >
          upload
        </ButtonIcon>
      </Tooltip>
    );

    const renderUploadTemplateButton = (
      <Tooltip content="Upload Template">
        <ButtonIcon
          kind="neutral"
          onClick={() => dispatch(setShowUploadTemplateModal(true))}
        >
          backup
        </ButtonIcon>
      </Tooltip>
    );

    const renderEmailCorrespondence = (
      <Tooltip content="Email Correspondence">
        <ButtonIcon
          kind={showEmailDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowEmailDrawer(!showEmailDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "email")}
        >
          email
        </ButtonIcon>
      </Tooltip>
    );

    const renderDownloadButton = (
      <Tooltip content="Download">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowDownloadModal(true));
          }}
        >
          download
        </ButtonIcon>
      </Tooltip>
    );

    const renderDownloadTemplateButton = (
      <Tooltip content="Download Template">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setDownloadTemplate(true));
          }}
        >
          download
        </ButtonIcon>
      </Tooltip>
    );

    const renderAddPolicyButton = (
      <Tooltip content="Add Policy">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowAddPolicyModal(true));
          }}
        >
          post_add
        </ButtonIcon>
      </Tooltip>
    );

    const renderSendCMSFile = (
      <Tooltip content="Send CMS File">
        <ButtonIcon
          kind="neutral"
          onClick={() => {
            dispatch(setShowSendCMSFileModal(true));
          }}
        >
          send
        </ButtonIcon>
      </Tooltip>
    );

    const renderResolveCMSError = (
      <Tooltip content="Resolve CMS Errors">
        <ButtonIcon
          onClick={() => {
            dispatch(setShowResolveCMSErrorModal(true));
          }}
        >
          error
        </ButtonIcon>
      </Tooltip>
    );

    const renderLegalNotesButton = (
      <Tooltip content="Legal Notes">
        <ButtonIcon
          kind={showLegalNotes ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowLegalNotes(!showLegalNotes));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "legalNotes")}
        >
          gavel
        </ButtonIcon>
      </Tooltip>
    );

    const renderClinicianNotesButton = (
      <Tooltip content="Clinician Notes">
        <ButtonIcon
          kind={showClinicianNotes ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClinicianNotes(!showClinicianNotes));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clinicianNotes")}
        >
          medical_information
        </ButtonIcon>
      </Tooltip>
    );

    const renderMedicalHistoryButton = (
      <Tooltip content="Medical History">
        <ButtonIcon
          kind={showMedicalHistory ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowMedicalHistory(!showMedicalHistory));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "medicalHistory")}
        >
          inventory
        </ButtonIcon>
      </Tooltip>
    );

    const renderRxHistoryButton = (
      <Tooltip content="RX History">
        <ButtonIcon
          kind={showRxHistory ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowRxHistory(!showRxHistory));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "rxHistory")}
        >
          medication
        </ButtonIcon>
      </Tooltip>
    );

    const renderClientInstructionsButton = (
      <Tooltip content="Client Instructions">
        <ButtonIcon
          kind={showClientInfoDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClientInfoDrawer(!showClientInfoDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clientInfo")}
        >
          assignment
        </ButtonIcon>
      </Tooltip>
    );

    const renderDocumentsButton = (
      <Tooltip content="Documents">
        <ButtonIcon
          kind={showDocumentsDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowDocumentsDrawer(!showDocumentsDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "documents")}
        >
          topic
        </ButtonIcon>
      </Tooltip>
    );

    const renderTasksButton = (
      <Tooltip content="Tasks">
        <ButtonIcon
          kind={showTasksDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowTasksDrawer(!showTasksDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "tasks")}
        >
          task_alt
        </ButtonIcon>
      </Tooltip>
    );

    const renderClientFilesButton = (
      <Tooltip content="Client Files">
        <ButtonIcon
          kind={showClientFilesDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClientFilesDrawer(!showClientFilesDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clientFiles")}
        >
          folder
        </ButtonIcon>
      </Tooltip>
    );

    const renderClientDocumentsButton = (
      <Tooltip content="Client Documents">
        <ButtonIcon
          kind={showClientDocumentsDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClientDocumentsDrawer(!showClientDocumentsDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clientDocuments")}
        >
          topic
        </ButtonIcon>
      </Tooltip>
    );

    const renderClientFeedsButton = (
      <Tooltip content="Client Import Feeds">
        <ButtonIcon
          kind={showClientFeedsDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClientFeedsDrawer(!showClientFeedsDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clientFeeds")}
        >
          feed
        </ButtonIcon>
      </Tooltip>
    );

    const renderClientExportFeedsButton = (
      <Tooltip content="Client Export Feeds">
        <ButtonIcon
          kind={showClientExportFeedsDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(
              setShowClientExportFeedsDrawer(!showClientExportFeedsDrawer),
            );
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "clientExportFeeds")}
        >
          dynamic_feed
        </ButtonIcon>
      </Tooltip>
    );

    const renderClaimLinkingButton = (
      <Tooltip content="Manage Linked Claims">
        <ButtonIcon
          kind={showClaimLinkingDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowClaimLinkingDrawer(!showClaimLinkingDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "claimLinking")}
        >
          add_link
        </ButtonIcon>
      </Tooltip>
    );

    const renderRelatedFilesButton = (
      <Tooltip content="Related Files">
        <ButtonIcon
          kind={showRelatedFilesDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowRelatedFilesDrawer(!showRelatedFilesDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "relatedFiles")}
        >
          group
        </ButtonIcon>
      </Tooltip>
    );

    const renderReportsTableButton = (
      <Tooltip content="View Reports">
        <ButtonIcon
          kind={showReportsTableDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowReportsTableDrawer(!showReportsTableDrawer));
          }}
        >
          table_chart
        </ButtonIcon>
      </Tooltip>
    );

    const renderAddDevelopmentButton = (
      <Tooltip content="Add Development">
        <ButtonIcon kind="neutral" onClick={handleAddDevelopment}>
          note_add
        </ButtonIcon>
      </Tooltip>
    );

    const renderAddDecisionButton = (
      <Tooltip content="Add Decision">
        <ButtonIcon kind="neutral" onClick={handleAddDecision}>
          gavel
        </ButtonIcon>
      </Tooltip>
    );

    const renderOCRLetterButton = (
      <Tooltip content="Process CMS Letter">
        <ButtonIcon
          kind="neutral"
          onClick={() => setShowOCRMSADecisionModal(true)}
        >
          document_scanner
        </ButtonIcon>
      </Tooltip>
    );

    const renderGenerateFMAllocatorNotesButton = (
      <>
        <GenerateAllocatorNotesButton
          serviceId={
            msaId || lcpId || mcpId || amendedReviewId || documentManagementId
          }
          serviceType={
            msaId
              ? "MSA"
              : lcpId
                ? "LCP"
                : mcpId
                  ? "MCP"
                  : amendedReviewId
                    ? "AMENDED_REVIEW"
                    : documentManagementId
                      ? "DOCS"
                      : "MSA"
          }
        />
      </>
    );

    const renderGenerateFMDocumentButton = (
      <>
        {isMSAPage && msaId && (
          <GenerateDocumentButton serviceType="MSA" serviceId={msaId} />
        )}
        {isLCPPage && lcpId && (
          <GenerateDocumentButton serviceType="LCP" serviceId={lcpId} />
        )}
        {isMCPPage && mcpId && (
          <GenerateDocumentButton serviceType="MCP" serviceId={mcpId} />
        )}
        {isAmendedReviewPage && amendedReviewId && (
          <GenerateDocumentButton
            serviceType="AMENDED_REVIEW"
            serviceId={amendedReviewId}
          />
        )}
      </>
    );

    const renderGenerateMetadocCombinedPdfButton = metadocId ? (
      <GenerateCombinedPdfButton
        metadocId={metadocId}
        msaId={msaId || amendedReviewId}
        costProjectionId={lcpId || mcpId}
        extractOnly={isOperationsMetadocDetailPage}
      />
    ) : null;

    const renderGenerateLienDocumentButton = lienId ? (
      <GenerateLienDocumentButton lienId={lienId} lien={lienData} />
    ) : null;

    const renderTriggerOCRLienButton = (
      <Tooltip content="Trigger OCR">
        <ButtonIcon kind="neutral" onClick={() => setShowOCRModal(true)}>
          camera
        </ButtonIcon>
      </Tooltip>
    );

    const renderLienICDCodingButton = (
      <Tooltip content="ICD Coding">
        <ButtonIcon
          kind={showLienICDCodingDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowLienICDCodingDrawer(!showLienICDCodingDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "lienICDCoding")}
        >
          code
        </ButtonIcon>
      </Tooltip>
    );

    const renderBulkImportRREButton = (
      <Tooltip content="Bulk Import RREs">
        <ButtonIcon
          kind="neutral"
          onClick={() => setShowBulkImportRREModal(true)}
        >
          drive_folder_upload
        </ButtonIcon>
      </Tooltip>
    );

    const handleSendEmailClick = () => {
      const flow = getEmailFlowToOpen(
        canSendAckEmail,
        canSendRatedAgeRequest,
        canSendRequestForMaterials,
      );
      if (flow === "ackEmail") {
        setShowAckEmailModal(true);
      } else if (flow === "ratedAgeRequest") {
        setShowRatedAgeRequestModal(true);
      } else if (flow === "requestForMaterials") {
        setShowRequestMaterialsFromFileViewModal(true);
      } else {
        setShowEmailTypeSelectionModal(true);
      }
    };

    // One shared mail icon now covers all email flows; the picker modal is
    // what lets the user say which one they want instead of separate icons.
    const renderSendEmailButton = (
      <Tooltip content="Email Correspondence">
        <ButtonIcon kind="neutral" onClick={handleSendEmailClick}>
          forward_to_inbox
        </ButtonIcon>
      </Tooltip>
    );

    const renderRequestMaterialsEmailButton = (
      <Tooltip content="Request For Materials">
        <ButtonIcon
          kind="neutral"
          onClick={() => setShowRequestMaterialsModal(true)}
        >
          forward_to_inbox
        </ButtonIcon>
      </Tooltip>
    );

    const renderOCRQueryResultButton = (
      <Tooltip
        content={
          coverageComplete
            ? "Coverage marked complete"
            : noCoverage
              ? "No coverage selected"
              : "OCR Query Result"
        }
        place="left"
        positionStrategy="fixed"
      >
        <ButtonIcon
          kind="neutral"
          onClick={() => setShowOCRQueryResultModal(true)}
          disabled={coverageComplete || noCoverage}
        >
          document_scanner
        </ButtonIcon>
      </Tooltip>
    );

    const renderOCRRatedAgeButton = (
      <Tooltip content="OCR Rated Age" place="left" positionStrategy="fixed">
        <ButtonIcon
          kind="neutral"
          onClick={() => setShowOCRRatedAgeModal(true)}
        >
          document_scanner
        </ButtonIcon>
      </Tooltip>
    );

    const renderCPTDirectoryButton = (
      <Tooltip content="Code Directory">
        <ButtonIcon
          kind={showCPTCodesDrawer ? "active" : "neutral"}
          onClick={() => {
            dispatch(setShowCPTCodesDrawer(!showCPTCodesDrawer));
          }}
          onContextMenu={(e) => handleDrawerContextMenu(e, "cptCodes")}
        >
          menu_book
        </ButtonIcon>
      </Tooltip>
    );

    return {
      add: {
        shouldRender:
          (isRrePage ||
            isClaimsPage ||
            isClientPage ||
            isClaimantsPage ||
            ((isFilesListingPage || isIntakeListingPage) &&
              loggedInUser?.has_operations_authority) ||
            isEmployersPage) &&
          !isClaimDetailPage &&
          !isRreOverviewPage &&
          !isRreCMSSubmissionsPage &&
          !isRreCMSCommunicationPage &&
          !isRreImportPage &&
          !isRrePolicyPage &&
          !isRreTagsPage &&
          !isRreTinsPage,
        content: renderAddButton,
      },
      addTask: {
        shouldRender:
          ((isFileEditPage && fileStatus !== "U") ||
            isMsaSubPage ||
            isAuditOrConsultingPage ||
            isAmendedReviewPage ||
            isCostProjectionPage ||
            isAmendedReviewScreenPage ||
            isLienDetailPage ||
            isLienOverviewPage) &&
          !isClaimDetailPage,
        content: renderAddTaskButton,
      },
      sendToOps: {
        shouldRender: isYourTasksPage,
        content: renderSendToOpsButton,
      },
      error: {
        shouldRender: shouldShowValidation,
        content: renderErrorButton,
      },
      warning: {
        shouldRender: shouldShowValidation,
        content: renderWarningButton,
      },
      upload: {
        shouldRender: isImportsPage,
        content: renderUploadButton,
      },
      uploadTemplate: {
        shouldRender: isClientDetailPage,
        content: renderUploadTemplateButton,
      },
      download: {
        shouldRender: isImportsPage,
        content: renderDownloadButton,
      },
      downloadTemplate: {
        shouldRender: isClientDetailPage,
        content: renderDownloadTemplateButton,
      },
      addPolicy: {
        shouldRender: isPoliciesPage,
        content: renderAddPolicyButton,
      },
      sendCMSFile: {
        shouldRender: isRreCMSCommunicationPage,
        content: renderSendCMSFile,
      },
      showCMSError: {
        shouldRender:
          isClaimDetailPage &&
          claimHasCmsErrors &&
          !isClaimICDCodingPage &&
          !isClaimAuditHistoryPage &&
          !isClaimTranmissionHistoryPage &&
          !isClaimQueryResponsePage,
        content: renderResolveCMSError,
      },
      showLegalNotes: {
        shouldRender:
          isMSALegalReviewPage ||
          isAmendedReviewLegalReviewPage ||
          isCostProjectionLegalReviewPage,
        content: renderLegalNotesButton,
      },
      showClinicianNotes: {
        shouldRender:
          isMSAMedicalReviewPage ||
          isMSALegalReviewPage ||
          isMSARatedAgePage ||
          isAmendedReviewLegalReviewPage ||
          isAmendedReviewMedicalReviewPage ||
          isCostProjectionMedicalReviewPage ||
          isCostProjectionLegalReviewPage ||
          isDocumentManagementPage,
        content: renderClinicianNotesButton,
      },
      showMedicalHistory: {
        shouldRender:
          isMSAPage ||
          isCostProjectionPage ||
          isAmendedReviewPage ||
          isDocumentManagementPage,
        content: renderMedicalHistoryButton,
      },
      showRxHistory: {
        shouldRender:
          isMSAPage ||
          isCostProjectionPage ||
          isAmendedReviewPage ||
          isDocumentManagementPage,
        content: renderRxHistoryButton,
      },
      showClientInstructions: {
        shouldRender: isFutureMedicalServiceDetailPage || isMSAOverviewPage,
        content: renderClientInstructionsButton,
      },
      documents: {
        shouldRender:
          showMSAButtons ||
          isAuditOrConsultingPage ||
          isCostProjectionPage ||
          isFileEditPage ||
          isAmendedReviewPage ||
          isAmendedReviewScreenPage ||
          isLienOverviewPage ||
          isLienDetailPage ||
          isClaimDetailPage ||
          isRreDetailPage ||
          isDocumentManagementPage ||
          isMetaDocPage,
        content: renderDocumentsButton,
      },
      showUploadClaimsForFile: {
        shouldRender: isFileEditPage && fileHasSection111Service,
        content: renderUploadClaimsOnFileButton,
      },
      showBackgroundJobs: {
        shouldRender:
          isMSAPage ||
          isMCPPage ||
          isLCPPage ||
          isAmendedReviewPage ||
          isLienOverviewPage ||
          isLienDetailPage ||
          isDocumentManagementPage ||
          isFinancePage ||
          isFileEditPage ||
          isMetaDocPage,
        content: renderBackgroundJobsButton,
      },
      tasks: {
        shouldRender:
          (isAuditOrConsultingPage || isFileEditPage) && !isClaimDetailPage,
        content: renderTasksButton,
      },
      showLinkClaims: {
        shouldRender:
          (isFileEditPage &&
            !isClaimDetailPage &&
            fileData?.has_section_111_claim_linkage_available) ||
          isLienQueryPage,
        content: renderClaimLinkingButton,
      },
      showRelatedFiles: {
        shouldRender: isFileEditPage && !isClaimDetailPage,
        content: renderRelatedFilesButton,
      },
      showReportsTable: {
        shouldRender: isSection111Reporting,
        content: renderReportsTableButton,
      },
      // alternative add button, with border and padding
      addCentered: {
        shouldRender:
          isRreTagsPage ||
          isMSAQueryPage ||
          isCostProjectionQueryPage ||
          isAmendedReviewQueryPage ||
          isLienQueryPage ||
          isFileQueryPage ||
          isRreTinsPage ||
          isLienOverviewPage,
        content: renderAddButton,
      },
      archive: {
        shouldRender: isClaimsOverviewPage, // removed from file edit page - replaced by background jobs
        content: renderArchiveClaimButton,
      },
      renderEmailCorrespondence: {
        shouldRender: false,
        content: renderEmailCorrespondence,
      },
      generateFMDocument: {
        shouldRender:
          !isMSAMetadocDetailPage &&
          ((isMSAPage && Boolean(msaId)) ||
            (isLCPPage && Boolean(lcpId)) ||
            (isMCPPage && Boolean(mcpId)) ||
            (isAmendedReviewPage && Boolean(amendedReviewId))),
        content: renderGenerateFMDocumentButton,
      },
      generateFMAllocatorNotes: {
        shouldRender: false,
        content: renderGenerateFMAllocatorNotesButton,
      },
      cptDirectory: {
        shouldRender:
          isMSAPage || isLCPPage || isMCPPage || isAmendedReviewPage,
        content: renderCPTDirectoryButton,
      },
      generateLienDocument: {
        shouldRender: isLienDetailPage,
        content: renderGenerateLienDocumentButton,
      },
      generateMetadocCombinedPdf: {
        shouldRender: isMSAMetadocDetailPage || isOperationsMetadocDetailPage,
        content: renderGenerateMetadocCombinedPdfButton,
      },
      triggerOCRLien: {
        shouldRender: isLienDetailPage || isLienOverviewPage,
        content: renderTriggerOCRLienButton,
      },

      paymentHistory: {
        shouldRender: isLienDetailPage,
        content: renderPaymentHistoryButton,
      },
      addDevelopment: {
        shouldRender: isCMSStatusPage,
        content: renderAddDevelopmentButton,
      },
      addDecision: {
        shouldRender:
          isCMSStatusPage && Boolean(msaData?.decision_is_available),
        content: renderAddDecisionButton,
      },
      showLienICDCoding: {
        shouldRender: isLienDetailPage,
        content: renderLienICDCodingButton,
      },
      addMSAReReview: {
        shouldRender: isCMSStatusPage && Boolean(msaData?.re_review_available),
        content: renderAddMSAReReviewButton,
      },
      ocrMSADecision: {
        // Development no longer needs an existing card to process a letter
        // (completing OCR is what creates one), so that half only checks
        // its feature flag. Decision still needs an undecided decision to
        // exist, since there's no card for it to process a letter into
        // otherwise. Without this, a decision-only org with nothing
        // undecided would show the button and land on an empty chooser.
        shouldRender:
          isCMSStatusPage &&
          ((Boolean(undecidedMSADecision) &&
            Boolean(
              constants?.FEATURE_FLAGS?.[
                FEATURE_FLAGS.MSA_DECISION_OCR_ENABLED
              ],
            )) ||
            Boolean(
              constants?.FEATURE_FLAGS?.[
                FEATURE_FLAGS.MSA_DEVELOPMENT_OCR_ENABLED
              ],
            )),
        content: renderOCRLetterButton,
      },
      bulkImportRRE: {
        shouldRender:
          isRrePage &&
          !isRreDetailPage &&
          !isRreOverviewPage &&
          !isRreCMSSubmissionsPage &&
          !isRreCMSCommunicationPage &&
          !isRreImportPage &&
          !isRrePolicyPage &&
          !isRreTagsPage &&
          !isRreTinsPage,
        content: renderBulkImportRREButton,
      },
      sendEmail: {
        shouldRender:
          canSendAckEmail ||
          canSendRatedAgeRequest ||
          canSendRequestForMaterials,
        content: renderSendEmailButton,
      },
      requestMaterialsEmail: {
        shouldRender:
          (isMSAPage ||
            isAmendedReviewPage ||
            isCostProjectionPage ||
            isAmendedReviewScreenPage) &&
          Boolean(requestMaterialsServiceId) &&
          Boolean(requestMaterialsFileId) &&
          Boolean(requestMaterialsServiceType) &&
          Boolean(loggedInUser?.has_operations_authority) &&
          Boolean(
            constants?.FEATURE_FLAGS?.[
              FEATURE_FLAGS.REQUEST_FOR_MATERIALS_EMAIL_ENABLED
            ],
          ),
        content: renderRequestMaterialsEmailButton,
      },

      clientFiles: {
        shouldRender: isClientDetailPage,
        content: renderClientFilesButton,
      },
      clientDocuments: {
        shouldRender: isClientDetailPage || isClientInvoicingPage,
        content: renderClientDocumentsButton,
      },
      clientFeeds: {
        shouldRender: isClientDetailPage,
        content: renderClientFeedsButton,
      },
      clientExportFeeds: {
        shouldRender: isClientDetailPage,
        content: renderClientExportFeedsButton,
      },
      ocrQueryResult: {
        shouldRender:
          isMSAQueryPage ||
          isCostProjectionQueryPage ||
          isAmendedReviewQueryPage ||
          isLienQueryPage ||
          isFileQueryPage,
        content: renderOCRQueryResultButton,
      },
      ocrRatedAge: {
        shouldRender:
          (isMSAPage || isCostProjectionPage) &&
          isMSARatedAgePage &&
          Boolean(
            constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.RATED_AGE_OCR_ENABLED],
          ),
        content: renderOCRRatedAgeButton,
      },
    };
  }, [
    dispatch,
    currentFile,
    claimData,
    isClaimantsPage,
    isClaimsPage,
    isClientPage,
    isAmendedReviewScreenPage,
    isImportsPage,
    isPoliciesPage,
    isRreTagsPage,
    isEmployersPage,
    isRrePage,
    isRreCMSCommunicationPage,
    isRreImportPage,
    isRrePolicyPage,
    isRreCMSSubmissionsPage,
    isRreTinsPage,
    isRreOverviewPage,
    isMSALegalReviewPage,
    shouldShowValidation,
    validationErrors?.length,
    validationWarnings?.length,
    errorDrawerVisible,
    warningDrawerVisible,
    showLegalNotes,
    showClinicianNotes,
    showMedicalHistory,
    showRxHistory,
    showClientInfoDrawer,
    isFutureMedicalServiceDetailPage,
    selectedFileIds,
    showDocumentsDrawer,
    fileHasSection111Service,
    isFileEditPage,
    isFilesListingPage,
    isIntakeListingPage,
    showTasksDrawer,
    showEmailDrawer,
    isMSAPage,
    isMSALegalReviewPage,
    isMSAMedicalReviewPage,
    isMSAQueryPage,
    isCostProjectionQueryPage,
    isLienQueryPage,
    isFileQueryPage,
    isClaimsOverviewPage,
    isClaimCMPPage,
    isAuditOrConsultingPage,
    // generateMSAAllocatorNotes,
    msaId,
    isMSAOverviewPage,
    isCostProjectionPage,
    isCostProjectionMedicalReviewPage,
    isCostProjectionLegalReviewPage,
    isAmendedReviewPage,
    isAmendedReviewScreenPage,
    isAmendedReviewLegalReviewPage,
    isAmendedReviewMedicalReviewPage,
    isMSALegalReviewPage,
    noCoverage,
    coverageComplete,
    showAddCoverageModal,
    canSendAckEmail,
    canSendRatedAgeRequest,
    canSendRequestForMaterials,
    requestForMaterialsFileServiceOptions,
    currentService,
    isLienDetailPage,
    isLienOverviewPage,
    currentLienStatus,
    handleLienStatusChange,
    showLienPaymentHistoryDrawer,
    showLienICDCodingDrawer,
    showRelatedFilesDrawer,
    lienId,
    fileId,
    constants,
    undecidedMSADecision,
    showOCRModal,
    showDeleteLienModal,
    showOCRProgressModal,
    showBackgroundJobs,
    showBulkImportRREModal,
    isDocumentManagementPage,
    isFinancePage,
    isMetaDocPage,
    isMSAMetadocDetailPage,
    isOperationsMetadocDetailPage,
    metadocId,
    loggedInUser?.has_management_authority,
    loggedInUser?.has_operations_authority,
    showClientFilesDrawer,
    showClientDocumentsDrawer,
    showClientFeedsDrawer,
    showCPTCodesDrawer,
    showOCRQueryResultModal,
    isMSARatedAgePage,
    mcpId,
    lcpId,
    showOCRRatedAgeModal,
  ]);

  const sidebarContent = useMemo(() => {
    return (
      <div className="sidebar-content">
        <div className="top-buttons">
          {Object.keys(buttonRenderSettings)
            .filter(
              (key) =>
                ![
                  "addCentered",
                  "archive",
                  "ocrQueryResult",
                  "ocrRatedAge",
                ].includes(key),
            )
            .map((key) => {
              const button = buttonRenderSettings[key];
              return button.shouldRender ? (
                <span key={key}>{button.content}</span>
              ) : null;
            })}
        </div>

        <div className="middle-section">
          {buttonRenderSettings.addCentered.shouldRender &&
            buttonRenderSettings.addCentered.content}
          {buttonRenderSettings.ocrQueryResult.shouldRender &&
            buttonRenderSettings.ocrQueryResult.content}
          {buttonRenderSettings.ocrRatedAge.shouldRender &&
            buttonRenderSettings.ocrRatedAge.content}
        </div>

        <div className="bottom-buttons">
          {buttonRenderSettings.archive.shouldRender &&
            buttonRenderSettings.archive.content}

          {/* Future Medical Review Completion Buttons */}
          <FutureMedicalReviewButtons
            msaId={msaId}
            amendedReviewId={amendedReviewId}
            mcpId={mcpId}
            lcpId={lcpId}
          />

          {/* Future Medical status toggle button */}
          {/* only show on overview pages, not on lien detail pages */}
          {!isLienDetailPage && (
            <ServiceStatusToggle
              currentService={
                currentService as MSADataType | CostProjectionDataType
              }
              lienFileData={lienFileData}
              documentManagementFileData={documentManagementFileData}
            />
          )}

          {isLienDetailPage && currentLienStatus && (
            <LienStatusTrafficLight
              currentStatus={currentLienStatus}
              onStatusChange={handleLienStatusChange}
              onDelete={() => setShowDeleteLienModal(true)}
            />
          )}

          {isClientDetailPage && loggedInUser?.has_management_authority && (
            <Tooltip content="Delete Client" variant="error">
              <ButtonIcon
                kind="neutral"
                onClick={() => setShowDeleteClientModal(true)}
              >
                delete
              </ButtonIcon>
            </Tooltip>
          )}
        </div>
      </div>
    );
  }, [
    buttonRenderSettings,
    currentService,

    isLienDetailPage,
    currentLienStatus,
    handleLienStatusChange,
    showDeleteLienModal,
    handleDeleteLien,
    loggedInUser?.has_management_authority,
    showDeleteClientModal,
    handleDeleteClient,
  ]);

  // Creator is only a stand-in for the referring party on intake files (the
  // portal submitter). On Operations-created files it's just staff, so don't
  // fall back to it there.
  const referringPartyFallbackEmail =
    fileData?.source === "INTAKE" && typeof fileData?.creator === "object"
      ? fileData.creator?.email
      : undefined;

  return (
    <>
      <Sidebar position={"right"} isvisible={!!isVisible}>
        {sidebarContent}
      </Sidebar>
      <Drawer visible={errorDrawerVisible} type="error" />
      <Drawer visible={warningDrawerVisible} type="warning" />
      {showDeleteLienModal && (
        <KaboodleModal
          isOpen={showDeleteLienModal}
          onClose={() => setShowDeleteLienModal(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDeleteLien}>
                Confirm Delete
              </Button>
              <Button onClick={() => setShowDeleteLienModal(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Delete Lien">
            <p>
              Are you sure you want to delete this lien? This action cannot be
              undone.
            </p>
          </Card>
        </KaboodleModal>
      )}
      {showDeleteClientModal && (
        <KaboodleModal
          isOpen={showDeleteClientModal}
          onClose={() => setShowDeleteClientModal(false)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDeleteClient}>
                Confirm Delete
              </Button>
              <Button onClick={() => setShowDeleteClientModal(false)}>
                Cancel
              </Button>
            </div>
          }
        >
          <Card heading="Delete Client">
            <p>Are you sure you want to delete this client?</p>
            <p>This action cannot be undone.</p>
          </Card>
        </KaboodleModal>
      )}
      {showOCRModal && (
        <TriggerOCRModal
          isOpen={showOCRModal}
          onClose={() => setShowOCRModal(false)}
          onSuccess={() => {
            setShowOCRProgressModal(true);
            setShowOCRModal(false);
          }}
        />
      )}
      {/* MSA Type change modal moved to separate component */}
      {showBulkImportRREModal && (
        <BulkImportRREModal
          isOpen={showBulkImportRREModal}
          onClose={() => setShowBulkImportRREModal(false)}
        />
      )}
      {showOCRQueryResultModal && (
        <OCRQueryResultModal
          isOpen={showOCRQueryResultModal}
          onClose={() => setShowOCRQueryResultModal(false)}
          fileId={
            lienFileData?.file_id ||
            currentService?.file?.id ||
            fileData?.id ||
            fileId
          }
        />
      )}
      {showOCRRatedAgeModal && (
        <OCRRatedAgeModal
          isOpen={showOCRRatedAgeModal}
          onClose={() => setShowOCRRatedAgeModal(false)}
          fileId={currentService?.file?.id || fileId}
          msaId={msaId}
          costProjectionId={mcpId || lcpId}
        />
      )}
      {/* Gated only on showOCRMSADecisionModal, not on
      undecidedMSADecision: that's derived from a query that refetches
      mid-flow (e.g. right after a decision save), and re-checking it here on
      every render could unmount this modal out from under the user before
      its own onClose runs. */}
      {showOCRMSADecisionModal && (
        <OCRLetterModal
          isOpen={showOCRMSADecisionModal}
          onClose={() => setShowOCRMSADecisionModal(false)}
          msaId={(msaId || amendedReviewId) as string}
          decisionId={undecidedMSADecision?.id ?? null}
          decisionOCREnabled={Boolean(
            constants?.FEATURE_FLAGS?.[FEATURE_FLAGS.MSA_DECISION_OCR_ENABLED],
          )}
          developmentOCREnabled={Boolean(
            constants?.FEATURE_FLAGS?.[
              FEATURE_FLAGS.MSA_DEVELOPMENT_OCR_ENABLED
            ],
          )}
        />
      )}
      {/* Unlike AckEmailModal/RatedAgeRequestModal below, this one doesn't
      need a fileId (or any other prop) before it can render, so there's
      nothing to guard against; isOpen alone is enough for KaboodleModal to
      keep it out of the way when closed. */}
      <EmailTypeSelectionModal
        isOpen={showEmailTypeSelectionModal}
        onClose={() => setShowEmailTypeSelectionModal(false)}
        showAcknowledgementEmail={canSendAckEmail}
        showRatedAgeRequest={canSendRatedAgeRequest}
        showRequestForMaterials={canSendRequestForMaterials}
        onSelectAcknowledgementEmail={() => {
          setShowEmailTypeSelectionModal(false);
          setShowAckEmailModal(true);
        }}
        onSelectRatedAgeRequest={() => {
          setShowEmailTypeSelectionModal(false);
          setShowRatedAgeRequestModal(true);
        }}
        onSelectRequestForMaterials={() => {
          setShowEmailTypeSelectionModal(false);
          setShowRequestMaterialsFromFileViewModal(true);
        }}
      />
      {showAckEmailModal && fileId && (
        <AckEmailModal
          isOpen={showAckEmailModal}
          onClose={() => setShowAckEmailModal(false)}
          fileId={fileId}
          defaultToEmail={
            fileData?.referring_party_email ?? referringPartyFallbackEmail
          }
        />
      )}
      {showRequestMaterialsModal &&
        requestMaterialsServiceId &&
        requestMaterialsFileId &&
        requestMaterialsServiceType && (
          <RequestMaterialsModal
            isOpen={showRequestMaterialsModal}
            onClose={() => setShowRequestMaterialsModal(false)}
            serviceId={requestMaterialsServiceId}
            fileId={requestMaterialsFileId}
            serviceType={requestMaterialsServiceType}
            defaultToEmail={requestMaterialsDefaultToEmail}
          />
        )}
      {showRequestMaterialsFromFileViewModal && fileId && (
        <RequestMaterialsModal
          isOpen={showRequestMaterialsFromFileViewModal}
          onClose={() => setShowRequestMaterialsFromFileViewModal(false)}
          fileId={fileId}
          services={requestForMaterialsFileServiceOptions}
          defaultToEmail={
            fileData?.referring_party_email ?? referringPartyFallbackEmail
          }
        />
      )}
      {showRatedAgeRequestModal && fileId && (
        <RatedAgeRequestModal
          isOpen={showRatedAgeRequestModal}
          onClose={() => setShowRatedAgeRequestModal(false)}
          fileId={fileId}
        />
      )}
    </>
  );
};

export default KaboodleRightSideBar;
